import React, { useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import { getDateToDisplay } from "../../../../../utils/nutrition-helpers";
import { MealPlannerWeekSelector } from "./MealPlannerWeekSelector";
import { defaultFirebaseProjectId } from "../../../../../constants";

export const MealPlannerHeader = ({ mealPlan, locals }) => {
  const { query, mealPlanIsTemplate, origin, activeUserToRequest } = locals;
  const { athlete, plan: nutritionPlanId, viewMode } = query;
  const {
    referAsClients = false,
    _id: ownerId,
    avatar: ownerAvatar,
    firebaseProjectId,
  } = activeUserToRequest;
  const {
    _id: mealPlanId,
    utcStartDate,
    utcEndDate,
    published: initialPublished = false,
  } = mealPlan;
  const initialState = {
    published: initialPublished,
    moreOpened: false,
  };
  const [state, setState] = useState(initialState);
  const { published, moreOpened } = state;
  const referName = referAsClients ? "client" : "athlete";

  /**
   * Open modal to create the template
   */
  const handleOpenModal = (section) => {
    const body =
      section === "copy-meal-plan-from-template" && query.week
        ? { "modal[type]": section, week: query.week }
        : { "modal[type]": section };
    submit(`/actions/refresh/?`, {
      method: "GET",
      body,
    });
  };

  /**
   * Function to change the published status of the meal plan
   * @param {*} e
   */
  const handleCheckboxChange = (e) => {
    const value = e.target.checked;
    const action = value ? "publish" : "unpublish";
    if (
      process.browser &&
      confirm(`Are you sure you want to ${action} this plan?`)
    ) {
      setState((prevState) => ({
        ...prevState,
        published: value,
      }));

      const coachAvatar = ownerAvatar?.file ?? null;

      const firebaseProjectIdToUse =
        firebaseProjectId ?? defaultFirebaseProjectId;

      const body = {
        published: value,
        coachId: ownerId,
        utcStartDate,
        avatar: coachAvatar,
        firebaseProjectId: firebaseProjectIdToUse,
        athleteId: athlete,
      };
      submit(
        `/actions/meal-planner/update-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        }
      );
    }
  };

  /**
   * Function to delete a meal plan
   */
  const handleDelete = () => {
    if (
      process.browser &&
      confirm(`Are you sure you want to delete this meal plan?`)
    ) {
      submit(
        `/actions/meal-planner/delete-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}&nutritionPlanId=${nutritionPlanId}`,
        {
          method: "POST",
        }
      );
    }
  };

  return (
    <header
      className={!viewMode ? "meal-planner-header" : "meal-planner-header mb-8"}
    >
      <hgroup className="flex justify-center items-center gap-4">
        <SvgIcon
          src="/img/new-icons/back-button.svg"
          onClickFn={() => {
            if (nutritionPlanId) {
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  section: "",
                  plan: "",
                  mealPlan: "",
                  week: "",
                  tabView: "plans",
                  viewMode: "",
                },
              });
            } else {
              window.location.href = `${origin}/coaching-portal/all-templates?owner=coach&tabView=meal-plans-tab`;
            }
          }}
          classNames="cursor-pointer"
        />

        {utcStartDate && utcEndDate && (
          <span className="header-date">
            {getDateToDisplay(utcStartDate)} - {getDateToDisplay(utcEndDate)}
          </span>
        )}
      </hgroup>
      {!viewMode ? (
        <hgroup className="header-buttons">
          <div className="chip info">
            Inactive days are not part of this nutrition plan
          </div>
          <button
            data-button-type="secondary"
            onClick={() => handleOpenModal("data-source")}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
            <span>Data Source</span>
          </button>
          {nutritionPlanId && (
            <button
              data-button-type="secondary"
              disabled={mealPlanIsTemplate}
              onClick={() => handleOpenModal("save-as-template")}
            >
              {mealPlanIsTemplate && (
                <SvgIcon
                  src="/img/new-icons/checked-circle.svg"
                  alt="info"
                  noFilterOnLight={true}
                />
              )}
              <span>
                {mealPlanIsTemplate ? "Saved as Template" : "Save as Template"}
              </span>
            </button>
          )}

          <button
            data-button-type="secondary"
            onClick={() => handleOpenModal("copy-meal-plan-from-template")}
          >
            <span>Copy From Template</span>
          </button>

          {nutritionPlanId && (
            <button data-button-type="secondary">
              <span>Publish</span>
              <input
                className="ml-12 mr-2"
                type="checkbox"
                id="publish-button"
                name="publish"
                role="switch"
                onChange={handleCheckboxChange}
                checked={published}
              />
              <ToolTip
                id="meal-planner-publish-tooltip"
                className="help-tooltip"
                body={`Publishing a meal plan will send a notification to your ${referName} and allow them to view the meal plan. You can still make edits to the meal plan when it's published.`}
                multiline
                place="bottom"
              >
                <div className="help-tooltip-container">?</div>
              </ToolTip>
            </button>
          )}

          <details role="list" open>
            <summary
              onClick={(e) => {
                e.preventDefault();
                setState((prevState) => ({
                  ...prevState,
                  moreOpened: !moreOpened,
                }));
              }}
              onBlur={(e) => {
                e.preventDefault();
                setState((prevState) => ({
                  ...prevState,
                  moreOpened: false,
                }));
              }}
              aria-haspopup="listbox"
            >
              More
              <SvgIcon
                src={
                  moreOpened
                    ? "/img/svg/angle-up.svg"
                    : "/img/svg/angle-down.svg"
                }
              />
            </summary>
            <ul role="listbox" className={moreOpened ? "opened" : ""}>
              <li onMouseDown={handleDelete}>
                <span>Delete Meal Plan</span>
                <SvgIcon
                  src="/img/new-icons/red-trash.svg"
                  noFilterOnLight={true}
                />
              </li>
            </ul>
          </details>
        </hgroup>
      ) : (
        <MealPlannerWeekSelector mealPlan={mealPlan} query={query} />
      )}
    </header>
  );
};

MealPlannerHeader.propTypes = {
  mealPlan: PropTypes.object,
  locals: PropTypes.object,
};
